import Keycloak, { KeycloakTokenParsed, KeycloakInitOptions } from "keycloak-js";
import * as Sentry from "@sentry/react";
import appConfig from "./config"

const keycloak = new Keycloak({
    url: appConfig.AUTH_ENDPOINT,
    realm: appConfig.AUTH_REALM!,
    clientId: appConfig.AUTH_CLIENT_ID!,
});

export type KeycloakInitTokens = {
    idToken: KeycloakInitOptions['idToken'],
    token: KeycloakInitOptions['token'],
    refreshToken: KeycloakInitOptions['refreshToken'],
}

const getTokenRemainingValidity = (tokenParsed: KeycloakTokenParsed) => {
    if (!tokenParsed['exp']) {
        console.log('[KEYCLOAK] Unable to determine if token is expired as exp is not set');
        return -1;
    }

    const timeSkew = keycloak.timeSkew || 0;
    var expiresIn = tokenParsed['exp'] - Math.ceil(new Date().getTime() / 1000) + timeSkew;
    return expiresIn;
}

const isTokenValid = (tokenParsed: KeycloakTokenParsed, minValidity?: number) => {
    let remainingValidity = getTokenRemainingValidity(tokenParsed);
    if (minValidity) {
        remainingValidity -= minValidity;
    }
    return remainingValidity > 0;
}

const updateToken = keycloak.updateToken;
keycloak.updateToken = (minValidity: number) => {
    if (
        keycloak.refreshToken && 
        keycloak.refreshTokenParsed && 
        !isTokenValid(keycloak.refreshTokenParsed, 5)
    ) {
        keycloak.onAuthRefreshError && keycloak.onAuthRefreshError();
        return Promise.reject();
    }
    return updateToken(minValidity);
}

let refreshTokenExpiredTimeoutId: null | NodeJS.Timeout = null;

keycloak.onAuthRefreshError = () => {
    localStorage.removeItem('tokens');
    keycloak.logout();
};

keycloak.onAuthRefreshSuccess = () => {
    if (refreshTokenExpiredTimeoutId) {
        clearTimeout(refreshTokenExpiredTimeoutId);
        refreshTokenExpiredTimeoutId =  null;
    }
    if (keycloak.refreshTokenParsed && keycloak.timeSkew !== null) {
        const expiresIn = Math.max(0, getTokenRemainingValidity(keycloak.refreshTokenParsed) - 5);
        refreshTokenExpiredTimeoutId = setTimeout(() => {
            localStorage.removeItem('tokens');
            keycloak.logout();
        }, expiresIn * 1000);
    }
    
    const tokensString = JSON.stringify({
        idToken: keycloak.idToken,
        token: keycloak.token,
        refreshToken: keycloak.refreshToken
    });
    localStorage.setItem('tokens', tokensString);
}

keycloak.onAuthSuccess = () => {
    if (keycloak.onAuthRefreshSuccess) {
        keycloak.onAuthRefreshSuccess();
    }

    keycloak.loadUserProfile()
        .then((profile) => {
            const scope = Sentry.getCurrentScope();
            // only set the user to ensure we are not leaking user emails and names to sentry
            scope.setUser({id: profile.id});
        })
        .catch((error) => Sentry.captureException(error));
}

export default keycloak;